import { Component, Input, OnInit, OnDestroy, Signal, signal, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ControlService } from '../library/control.service';
import { RealtimeService } from '../library/realtime/realtime.service';
import { UserVideoService } from '../library/user-video.service';
import { DeviceSettingsService } from '../library/device-settings.service';
import { PlayerService } from '../library/player.service';
import { CallStoreService } from '../library/call-store/call-store.service';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { IPathwayRunStepResponse } from 'src/app/interfaces/ipathway-enrollment';
import { PathwayRoleplayService } from './services/pathway-roleplay.service';
import { RoleplayFooterState } from 'src/app/utils/enums/learner/pathway.enum';
import { SubtitleKind } from 'src/app/utils/enums/learner/subtitle-kind.enum';
import { PathwayStepType } from 'src/app/utils/enums/learner/pathway.enum';
import { PathwayRoleplayFooterService } from './services/pathway-roleplay-footer.service';

@Component({
  host: {
    class: 'theme-dark',
  },
  selector: 'app-call-pathway-roleplay',
  standalone: false,
  templateUrl: './call-pathway-roleplay.component.html',
  styleUrls: ['./call-pathway-roleplay.component.scss'],
  providers: [
    PathwayRoleplayService,
    PathwayRoleplayFooterService,
    { provide: TRANSLOCO_SCOPE, useValue: 'learningPathway' },
    { provide: TRANSLOCO_SCOPE, useValue: 'call' },
  ],
})
export class CallPathwayRoleplayComponent implements OnInit, OnDestroy {
  @Input() controlService: ControlService | undefined;
  @Input() realtimeService: RealtimeService | undefined;
  @Input() userVideoService: UserVideoService | undefined;
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Input() playerService: PlayerService | undefined;
  @Input() callStore: CallStoreService | undefined;
  @Input() enrollmentStepUuid: string = '';
  @Input() enrollmentUuid: string = '';
  @Input() stepType: string = PathwayConstants.STEP_TYPE_INTRO;
  @Input() callUuid: string = '';
  @Input() callType: string = '';
  @Input() runStepResponse: Signal<IPathwayRunStepResponse | null> | undefined;
  @Input() title: string = '';

  isLoading = signal(false);
  readonly isShowingResults = signal(false);

  readonly subtitleKind = SubtitleKind.Ready;

  get mainIsLoading() { return this.roleplayService.mainIsLoading; }
  get isPreamble() { return this.roleplayService.isPreamble; }
  get callDetails() { return this.roleplayService.callDetails; }
  get captionText() { return this.roleplayService.captionText; }
  protected get isStepTypePracticeCall() { return this.stepType === PathwayStepType.PRACTICE_CALL; }
  protected get shouldShowCallDetails() { return this.isStepTypePracticeCall && !this.isPreamble() && this.callDetails() !== null; }

  @Output() settingsClicked = new EventEmitter<void>();
  @Output() leaveStepClicked = new EventEmitter<void>();

  handleSettingsClick(): void { this.settingsClicked.emit(); }
  handleLeaveStepClick(): void { this.leaveStepClicked.emit(); }

  @Output() confirmActionClicked = new EventEmitter<void>();
  
  constructor(
    public readonly roleplayService: PathwayRoleplayService,
    public readonly footerService: PathwayRoleplayFooterService,
    private readonly route: ActivatedRoute,
    private readonly translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    this.footerService.init({ controlService: this.controlService, callStore: this.callStore });
    this.translationService.loadScopedTranslation('learningPathway');
    this.translationService.loadScopedTranslation('call');

    const moduleAssignmentUuid =
      this.callStore?.getModuleAssignmentUuid() ||
      this.route.snapshot.queryParams['moduleAssignmentUuid'] ||
      '';

    this.roleplayService.init({
      moduleAssignmentUuid,
      enrollmentStepUuid: this.enrollmentStepUuid,
      enrollmentUuid: this.enrollmentUuid,
      stepType: this.stepType,
      runStepResponse: this.runStepResponse,
      realtimeService: this.realtimeService,
      controlService: this.controlService,
      callStore: this.callStore,
    });
  }

  protected handleCallDetailsModalOpened(): void {
    if (this.realtimeService && !this.callStore?.getIsPaused()) {
      this.realtimeService.pauseCall();
    }
  }

 handleLaunchSimulationClick(): void {
    this.roleplayService.launchSimulationFromPreamble();
  }

  handleResultActionClick(): void {
    this.confirmActionClicked.emit();
  }

  handleOnConfirmActionClick(): void {
    this.isShowingResults.set(true);
    this.roleplayService.footerState.set(RoleplayFooterState.Result);
  }

  ngOnDestroy(): void {
    this.roleplayService.destroy();
  }
}
